/**
 * HologramFigure — CSS+SVG cinematic hologram for the Smithers section.
 * - Octahedron silhouette + concentric rotating rings + particle halo
 * - Cyan glow, slow rotation, depth pulse
 * - All CSS animations — light on perf, no R3F dependency
 */

export default function HologramFigure() {
    return (
        <div
            data-testid="smithers-hologram"
            className="relative w-full aspect-square max-w-[420px] mx-auto"
            aria-hidden="true"
        >
            {/* Outer ring rotating slowly (clockwise) */}
            <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ animation: "tca-spin-slow 36s linear infinite" }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full">
                    <defs>
                        <radialGradient id="ringGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="80%" stopColor="rgba(0,224,232,0)" />
                            <stop offset="100%" stopColor="rgba(0,224,232,0.6)" />
                        </radialGradient>
                    </defs>
                    <circle cx="100" cy="100" r="92" fill="none" stroke="rgba(0,224,232,0.35)" strokeWidth="0.6" strokeDasharray="2 4" />
                    {/* tick marks */}
                    {[...Array(36)].map((_, i) => {
                        const a = (i * 360) / 36;
                        const long = i % 3 === 0;
                        return (
                            <line
                                key={i}
                                x1="100"
                                y1="6"
                                x2="100"
                                y2={long ? 14 : 10}
                                stroke={long ? "rgba(0,224,232,0.65)" : "rgba(0,224,232,0.3)"}
                                strokeWidth="0.7"
                                transform={`rotate(${a} 100 100)`}
                            />
                        );
                    })}
                </svg>
            </div>

            {/* Mid ring rotating counter-clockwise */}
            <div
                className="absolute inset-[12%] flex items-center justify-center"
                style={{ animation: "tca-spin-rev 22s linear infinite" }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full">
                    <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(0,224,232,0.55)" strokeWidth="1.2" />
                    {/* nodes */}
                    {[0, 60, 120, 180, 240, 300].map((deg, i) => {
                        const r = 80;
                        const x = 100 + r * Math.cos((deg * Math.PI) / 180);
                        const y = 100 + r * Math.sin((deg * Math.PI) / 180);
                        return (
                            <circle key={i} cx={x} cy={y} r="2.5" fill="rgba(110,247,255,0.95)" style={{ filter: "drop-shadow(0 0 6px rgba(0,224,232,0.85))" }} />
                        );
                    })}
                </svg>
            </div>

            {/* Inner pulse ring */}
            <div
                className="absolute inset-[28%] flex items-center justify-center"
                style={{ animation: "tca-pulse-soft 4.2s ease-in-out infinite" }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full">
                    <circle cx="100" cy="100" r="68" fill="none" stroke="rgba(0,224,232,0.30)" strokeWidth="0.8" strokeDasharray="6 6" />
                </svg>
            </div>

            {/* Octahedron silhouette */}
            <div
                className="absolute inset-[34%] flex items-center justify-center"
                style={{
                    animation: "tca-spin-slow 18s linear infinite reverse",
                    filter: "drop-shadow(0 0 24px rgba(0,224,232,0.55))",
                }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full">
                    <g fill="none" stroke="rgba(110,247,255,0.85)" strokeWidth="1.6" strokeLinejoin="round">
                        {/* Front face */}
                        <polygon points="100,28 152,100 100,172 48,100" />
                        {/* Top diamond strut */}
                        <line x1="100" y1="28" x2="100" y2="172" opacity="0.55" />
                        <line x1="48" y1="100" x2="152" y2="100" opacity="0.55" />
                        {/* Back face hint */}
                        <polygon points="100,46 138,100 100,154 62,100" stroke="rgba(0,224,232,0.45)" strokeDasharray="2 4" />
                    </g>
                </svg>
            </div>

            {/* Center core glow */}
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-tca-cyan-hi"
                style={{
                    boxShadow:
                        "0 0 16px rgba(110,247,255,0.95), 0 0 36px rgba(0,224,232,0.6), 0 0 72px rgba(0,224,232,0.3)",
                    animation: "tca-pulse-soft 2.6s ease-in-out infinite",
                }}
            />

            {/* Particle halo (8 dots orbiting) */}
            <div
                className="absolute inset-0"
                style={{ animation: "tca-spin-slow 40s linear infinite" }}
            >
                {[...Array(8)].map((_, i) => {
                    const a = (i * 360) / 8;
                    const x = 50 + 49 * Math.cos((a * Math.PI) / 180);
                    const y = 50 + 49 * Math.sin((a * Math.PI) / 180);
                    return (
                        <span
                            key={i}
                            className="absolute w-1 h-1 rounded-full bg-tca-cyan-hi"
                            style={{
                                top: `${y}%`,
                                left: `${x}%`,
                                boxShadow: "0 0 6px rgba(0,224,232,0.8)",
                                opacity: 0.7,
                            }}
                        />
                    );
                })}
            </div>

            {/* Inline keyframes (scoped via CSS class names; falls through to global if defined) */}
            <style>{`
                @keyframes tca-spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes tca-spin-rev {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(-360deg); }
                }
                @keyframes tca-pulse-soft {
                    0%, 100% { opacity: 0.85; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.06); }
                }
            `}</style>
        </div>
    );
}
